import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, AlertCircle } from "lucide-react";

// Import child components
import ProjectTabs, { ProjectTab } from "../components/projects/ProjectTabs";
import ProjectCard from "../components/projects/ProjectCard";
import EmptyState from "../components/projects/EmptyState";
import ProjectsSkeleton from "../components/projects/ProjectsSkeleton";

// Import hooks and types
import {
  useGetUserProjects,
  UserProject,
  ProjectDifficulty,
} from "../lib/api/projects";
import { useAuth } from "../context/AuthContext";

const Projects: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<ProjectTab>(() => {
    const tab = searchParams.get("tab");
    if (tab === "pending" || tab === "my_projects") return tab;
    return "available";
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [difficultyFilter, setDifficultyFilter] = useState<
    ProjectDifficulty | ""
  >("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch projects
  const { data, isLoading, isError, error } = useGetUserProjects(user?.id);

  // Debug logging
  useEffect(() => {
    console.log("Query state:", { data, isLoading, isError, error });
  }, [data, isLoading, isError, error]);

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  // Get counts with safe defaults
  const counts = useMemo(
    () => ({
      available: data?.counts?.available ?? 0,
      pending: data?.counts?.pending ?? 0,
      myProjects: data?.counts?.myProjects ?? 0,
    }),
    [data]
  );

  // Get projects for current tab
  const currentProjects = useMemo((): UserProject[] => {
    if (!data) return [];

    switch (activeTab) {
      case "available":
        return data.available ?? [];
      case "pending":
        return data.pending ?? [];
      case "my_projects":
        return data.myProjects ?? [];
      default:
        return [];
    }
  }, [data, activeTab]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return currentProjects.filter((userProject) => {
      const project = userProject?.project;
      if (!project) return false;

      // Search filter
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        const matchesSearch =
          project.title?.toLowerCase().includes(searchLower) ||
          project.description?.toLowerCase().includes(searchLower) ||
          project.category?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Difficulty filter
      if (difficultyFilter && project.difficulty !== difficultyFilter) {
        return false;
      }

      return true;
    });
  }, [currentProjects, debouncedSearch, difficultyFilter]);

  const handleTabChange = (tab: ProjectTab) => {
    setActiveTab(tab);
    setSearchQuery("");
    setDebouncedSearch("");
    setDifficultyFilter("");
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setDifficultyFilter("");
  };

  const hasActiveFilters = searchQuery !== "" || difficultyFilter !== "";

  // Only show error if there's actually an error and no data
  if (isError && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Error Loading Projects
          </h2>
          <p className="text-gray-500">
            There was an error loading your projects. Please try again later.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {error?.message || "Unknown error"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Projects
            </h1>
            <p className="text-gray-500 mt-1">
              Browse and manage your projects
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Tabs and Search Bar */}
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Tabs */}
            <ProjectTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              counts={counts}
            />

            {/* Search and Filter */}
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 lg:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full lg:w-64 pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2.5 rounded-xl border transition-all ${
                  showFilters || hasActiveFilters
                    ? "border-[#8B52FF] bg-[#8B52FF]/5 text-[#8B52FF]"
                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                }`}
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-end gap-4">
              <div className="min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Difficulty
                </label>
                <select
                  value={difficultyFilter}
                  onChange={(e) =>
                    setDifficultyFilter(
                      e.target.value as ProjectDifficulty | ""
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] bg-white"
                >
                  <option value="">All Levels</option>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>

        {/* Projects Grid */}
        <div className="p-4 sm:p-6">
          {isLoading ? (
            <ProjectsSkeleton />
          ) : filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredProjects.map((userProject) => (
                <ProjectCard
                  key={userProject._id}
                  userProject={userProject}
                  variant={activeTab}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              type={activeTab}
              hasFilters={hasActiveFilters}
              onClearFilters={clearFilters}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Projects;
